/**
 * Module 'agent-install' (R-ON-9, ADR 0099): a person asks Orgus to install a coding agent's CLI
 * (Claude Code, Codex, Gemini CLI) on a machine that runs the Orgus agent, and the machine's agent
 * installs it for its own user, from the vendor's official source only.
 *
 * What the hub may say is only WHICH agent: the request carries a kind and an id, never a URL, a
 * file or a command. Every source is on an allow-list built into each agent (agent-ts
 * src/ext/agent-install/sources.ts, the Go agent internal/agentinstall), pinned in ADR 0099 with
 * the date it was read from the vendor's own docs. AGENT_INSTALL_ORIGINS below is the same list
 * of origins, so a server can refuse to record a source that is not on it.
 *
 * On the wire:
 *
 *   hub -> agent  { ext: 'agent-install', type: 'install', id, data: AgentInstallRequest }
 *   agent -> hub  { type: 'progress', id, data: AgentInstallProgress }     while it works
 *   agent -> hub  { type: 'result',   id, data: AgentInstallResult }       once, at the end
 *   agent -> hub  { type: 'refused',  id, data: ExtRefusal }               the ceiling refused it
 *   agent -> hub  { type: 'status',   data: AgentInstallStatusReport }     on start, after each
 *                                                                          install, and on 'status'
 *   hub -> agent  { type: 'status' }                                       look again now
 *
 * A machine's ceiling forbids installs with 'ext:agent-install' (or '*'): the agent answers
 * 'refused' and installs nothing. It is not in the default ceiling: an install is a person's
 * request, audited on the hub, from a vendor source the agent pins itself.
 *
 * A node is untrusted: a server keeps what an agent sends only through the sanitisers here.
 */
import type { Id, Ts } from '../index.ts';

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

export const AGENT_INSTALL_EXT = 'agent-install';
/** The ceiling entry that forbids installs on a machine. */
export const AGENT_INSTALL_CEILING = 'ext:agent-install';

export const AGENT_INSTALL_TYPE = 'install';
export const AGENT_INSTALL_PROGRESS = 'progress';
export const AGENT_INSTALL_RESULT = 'result';
export const AGENT_INSTALL_STATUS = 'status';

/** The agents Orgus can install. Every other kind is installed by the person. */
export type InstallableAgentKind = 'claude-code' | 'codex' | 'gemini-cli';
export const INSTALLABLE_AGENT_KINDS: readonly InstallableAgentKind[] = ['claude-code', 'codex', 'gemini-cli'];

export function isInstallableAgentKind(v: unknown): v is InstallableAgentKind {
  return typeof v === 'string' && (INSTALLABLE_AGENT_KINDS as readonly string[]).includes(v);
}

/**
 * How each agent is installed, in order of preference (ADR 0099 decision 1):
 *  - 'verified-binary': the vendor's own binary, its SHA-256 checked against the vendor's
 *    published manifest before it runs (Claude Code; then the binary's own `install` lays it out).
 *  - 'vendor-installer': the vendor's own installer script, fetched only from the pinned HTTPS
 *    origin and run for this user with its arguments as a list; the script checks the vendor's
 *    published checksums itself (Codex on macOS and Linux: its standalone layout is what its
 *    self-update needs; on Windows Orgus does not install Codex, see 'install-in-terminal').
 *  - 'npm': the vendor's npm package from the public registry, into a per-user prefix; npm
 *    checks the registry's integrity hash (Gemini CLI; needs Node.js).
 */
export type AgentInstallMethod = 'verified-binary' | 'vendor-installer' | 'npm';
export const AGENT_INSTALL_METHODS: readonly AgentInstallMethod[] = ['verified-binary', 'vendor-installer', 'npm'];

export const AGENT_INSTALL_METHOD_OF: Readonly<Record<InstallableAgentKind, AgentInstallMethod>> = {
  'claude-code': 'verified-binary',
  codex: 'vendor-installer',
  'gemini-cli': 'npm',
};

/**
 * Every HTTPS origin an agent may fetch from for each kind (redirects included). Pinned 21 Oct
 * 2026 (ADR 0099). An agent refuses any URL, or redirect, outside its kind's list; a server
 * refuses to record a source outside it.
 */
export const AGENT_INSTALL_ORIGINS: Readonly<Record<InstallableAgentKind, readonly string[]>> = {
  'claude-code': ['https://downloads.claude.ai'],
  // The documented URL (chatgpt.com) answers with a redirect to releases.openai.com.
  codex: ['https://chatgpt.com', 'https://releases.openai.com'],
  'gemini-cli': ['https://registry.npmjs.org'],
};

/**
 * The origin of an https URL ('https://host'), or null for anything else: another scheme, a port,
 * credentials, a backslash or a blank anywhere. Plain text matching (no URL parser), so the Go
 * agent can match it exactly (agentinstall.OriginOf).
 */
export function installUrlOrigin(url: unknown): string | null {
  if (typeof url !== 'string' || url.length > 500) return null;
  const m = /^https:\/\/([A-Za-z0-9.-]+)(?:[/?#][^\s\\]*)?$/.exec(url);
  return m ? `https://${m[1]!.toLowerCase()}` : null;
}

/** True when `url` is an https URL on one of the kind's pinned origins (no credentials, no port). */
export function isAllowedInstallUrl(kind: InstallableAgentKind, url: unknown): boolean {
  const origin = installUrlOrigin(url);
  return !!origin && AGENT_INSTALL_ORIGINS[kind].includes(origin);
}

/** Size and time bounds every agent applies (ADR 0099 decision 1). */
export const AGENT_INSTALL_LIMITS = Object.freeze({
  /** A binary or package larger than this is refused (Claude Code's binaries are about 300 MB). */
  binaryBytes: 640 * 1024 * 1024,
  /** A vendor installer script larger than this is refused. */
  scriptBytes: 512 * 1024,
  /** A version file, manifest or registry document larger than this is refused. */
  metadataBytes: 1024 * 1024,
  /** A whole download may take this long. */
  downloadMs: 20 * 60_000,
  /** A download that receives nothing for this long is abandoned. */
  idleMs: 60_000,
  /** The installer step (`claude install`, the vendor script, npm) may take this long. */
  installMs: 10 * 60_000,
  /** `<cli> --version` after the install may take this long. */
  versionMs: 30_000,
  /** At most this many redirects, each one checked against the allow-list. */
  redirects: 5,
  /** Gemini CLI's npm package says engines.node >= 20. */
  nodeMajor: 20,
});

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

/** hub -> agent: install this agent. Nothing else: no URL, file or command. */
export interface AgentInstallRequest {
  /** The hub's id for this install; progress and the result carry it back. */
  installId: string;
  kind: InstallableAgentKind;
  /** Who asked, for the agent's own log ('srikanth', 'policy: install agents automatically'). */
  requestedBy: string;
}

export type AgentInstallPhase = 'checking' | 'downloading' | 'verifying' | 'installing' | 'checking-version';
export const AGENT_INSTALL_PHASES: readonly AgentInstallPhase[] = ['checking', 'downloading', 'verifying', 'installing', 'checking-version'];

export const AGENT_INSTALL_PHASE_LABEL: Record<AgentInstallPhase, string> = {
  checking: 'Checking',
  downloading: 'Downloading',
  verifying: 'Verifying',
  installing: 'Installing',
  'checking-version': 'Checking the version',
};

/** agent -> hub while an install runs. */
export interface AgentInstallProgress {
  installId: string;
  kind: InstallableAgentKind;
  phase: AgentInstallPhase;
  /** Bytes downloaded so far, and the expected total when known (phase 'downloading'). */
  bytes?: number;
  total?: number;
}

/** Where an install came from, as the agent saw it: recorded in the audit with the result. */
export interface AgentInstallSource {
  method: AgentInstallMethod;
  /** The file the agent fetched and checked: the binary, the vendor script or the registry document. */
  url: string;
  /** SHA-256 (hex) of that file as downloaded. */
  sha256: string | null;
  /** npm only: the registry's integrity string for the package (sha512-...). */
  integrity?: string | null;
  /** The version the source named (the manifest's, the registry's), when known before installing. */
  version?: string | null;
}

export type AgentInstallErrorCode =
  | 'unknown-kind'
  | 'unsupported-platform'
  | 'needs-node'
  | 'already-installed'
  | 'busy'
  | 'ceiling'
  | 'source-refused'
  | 'download-failed'
  | 'too-large'
  | 'timeout'
  | 'checksum-mismatch'
  | 'install-failed'
  | 'version-check-failed'
  | 'offline'
  | 'install-in-terminal';

export const AGENT_INSTALL_ERROR_CODES: readonly AgentInstallErrorCode[] = [
  'unknown-kind',
  'unsupported-platform',
  'needs-node',
  'already-installed',
  'busy',
  'ceiling',
  'source-refused',
  'download-failed',
  'too-large',
  'timeout',
  'checksum-mismatch',
  'install-failed',
  'version-check-failed',
  'offline',
  'install-in-terminal',
];

export interface AgentInstallError {
  what: string;
  why: string;
  next: string;
  code: AgentInstallErrorCode;
}

/** agent -> hub, once per install. */
export interface AgentInstallResult {
  installId: string;
  kind: InstallableAgentKind;
  ok: boolean;
  /** The version the installed CLI reports (`<cli> --version`), on success. */
  version: string | null;
  source?: AgentInstallSource;
  error?: AgentInstallError;
}

/**
 * Why an agent cannot be installed on this machine at all (status), before anyone asks.
 * 'install-in-terminal': Orgus does not run the vendor's installer here, the person installs it
 * from their own terminal with AGENT_TERMINAL_INSTALL_COMMAND (Codex on Windows: its only
 * installer is a PowerShell script, and an Orgus agent never starts a script host a person does
 * not see, R-EN-3).
 */
export type AgentInstallBlock = 'needs-node' | 'unsupported-platform' | 'install-in-terminal';
export const AGENT_INSTALL_BLOCKS: readonly AgentInstallBlock[] = ['needs-node', 'unsupported-platform', 'install-in-terminal'];

/**
 * The vendor's own one-line install, shown as text for a person to run in their own terminal where
 * an agent is 'install-in-terminal'. Never run by Orgus. Codex on Windows: the PowerShell line from
 * github.com/openai/codex ("Installing and running Codex CLI"), pinned 21 Oct 2026 (ADR 0099).
 */
export const CODEX_WINDOWS_INSTALL_COMMAND = 'irm https://chatgpt.com/codex/install.ps1 | iex';

export interface AgentInstallKindStatus {
  kind: InstallableAgentKind;
  installed: boolean;
  version: string | null;
  /** This machine's agent can install it here (it is not installed and nothing blocks it). */
  installable: boolean;
  blocked?: AgentInstallBlock;
}

/** agent -> hub: what this machine's agent can install, so the console offers Install only where it works. */
export interface AgentInstallStatusReport {
  checkedAt: Ts;
  kinds: AgentInstallKindStatus[];
}

// ---------------------------------------------------------------------------
// Words: both agents build them the same way (the Go agent: agentinstall.ErrorFor)
// ---------------------------------------------------------------------------

const LABEL: Record<InstallableAgentKind, string> = { 'claude-code': 'Claude Code', codex: 'Codex', 'gemini-cli': 'Gemini CLI' };

/** The words for a failed or refused install. `detail` is a short, plain reason from the step that failed. */
export function agentInstallError(code: AgentInstallErrorCode, kind: InstallableAgentKind | string, detail = ''): AgentInstallError {
  const name = isInstallableAgentKind(kind) ? LABEL[kind] : 'That agent';
  const what = `${name} was not installed.`;
  const d = detail ? ` ${detail.replace(/\s+/g, ' ').trim().replace(/\.?$/, '.')}` : '';
  switch (code) {
    case 'unknown-kind':
      return { what, why: 'Orgus installs Claude Code, Codex and Gemini CLI only.', next: 'Install other agents on that computer yourself.', code };
    case 'unsupported-platform':
      return { what, why: `The vendor publishes no build for this computer's system.${d}`, next: 'Install it on that computer by hand, or use another machine.', code };
    case 'needs-node':
      return { what, why: `Gemini CLI runs on Node.js ${AGENT_INSTALL_LIMITS.nodeMajor} or later, and this computer has none.${d}`, next: 'Install Node.js from nodejs.org on that computer, then try again.', code };
    case 'already-installed':
      return { what, why: `It is already installed on this computer.${d}`, next: 'It updates itself. Sign in if it is not signed in yet.', code };
    case 'busy':
      return { what, why: 'Another install is running on this computer.', next: 'Wait for it to finish, then try again.', code };
    case 'ceiling':
      return { what, why: `This computer's ceiling refuses installs (${AGENT_INSTALL_CEILING}).`, next: `Only someone on that computer can allow it: orgus agent ceiling allow ${AGENT_INSTALL_CEILING}.`, code };
    case 'source-refused':
      return { what, why: `A download left the vendor's pinned address.${d}`, next: 'Nothing was installed. Try again later; if it keeps happening, install it by hand from the vendor.', code };
    case 'download-failed':
      return { what, why: `The download from the vendor failed.${d}`, next: 'Check that computer can reach the internet, then try again.', code };
    case 'too-large':
      return { what, why: `The download was larger than Orgus accepts.${d}`, next: 'Nothing was installed. Install it by hand from the vendor.', code };
    case 'timeout':
      return { what, why: `A step took too long.${d}`, next: 'Try again when that computer has a faster connection.', code };
    case 'checksum-mismatch':
      return { what, why: `The download did not match the checksum the vendor published.${d}`, next: 'Nothing was run. Try again later; if it keeps happening, tell the vendor.', code };
    case 'install-failed':
      return { what, why: `The vendor's installer stopped with an error.${d}`, next: 'Try again; if it fails again, install it by hand on that computer.', code };
    case 'version-check-failed':
      return { what, why: `The installed command did not report a version.${d}`, next: 'Open a new terminal on that computer and run it once; reinstall by hand if it does not start.', code };
    case 'offline':
      return { what, why: 'The machine went offline before it answered.', next: 'Try again when it is back online.', code };
    case 'install-in-terminal':
      return { what, why: `Orgus does not run ${name}'s installer on Windows.`, next: `Install it from your own terminal on that computer: run ${CODEX_WINDOWS_INSTALL_COMMAND} in PowerShell.`, code };
  }
}

/** The command that signs an agent in on its own machine. */
export const AGENT_SIGN_IN_COMMAND: Readonly<Record<InstallableAgentKind, string>> = {
  'claude-code': 'claude auth login',
  codex: 'codex login',
  'gemini-cli': 'gemini',
};

// ---------------------------------------------------------------------------
// Hub side: records, settings, state
// ---------------------------------------------------------------------------

export type AgentInstallState = 'installing' | 'installed' | 'failed';

/** One install the hub started (kept per machine and kind, the latest). */
export interface AgentInstallRecord {
  id: string;
  machineId: Id;
  kind: InstallableAgentKind;
  state: AgentInstallState;
  phase: AgentInstallPhase | null;
  bytes?: number;
  total?: number;
  /** Who asked: a person's id, or 'policy' for an automatic install on a new machine. */
  requestedBy: string;
  auto: boolean;
  requestedAt: Ts;
  finishedAt: Ts | null;
  version: string | null;
  source?: AgentInstallSource;
  error?: AgentInstallError;
}

/**
 * The admin-only setting "Install agents automatically on new machines" (ADR 0099 decision 3).
 * Off by default. `labels` is the machine group: empty means every new machine; otherwise only
 * new machines that carry one of these labels.
 */
export interface AgentInstallSettings {
  autoInstall: boolean;
  kinds: InstallableAgentKind[];
  labels: string[];
  updatedAt: Ts | null;
  updatedBy: string | null;
}

export const AGENT_INSTALL_SETTINGS_DEFAULT: AgentInstallSettings = { autoInstall: false, kinds: ['claude-code'], labels: [], updatedAt: null, updatedBy: null };

/** state.ext['agent-install']. */
export interface AgentInstallHubState {
  installs: AgentInstallRecord[];
  /** What each machine's agent said it can install, by machine id. */
  status: Record<Id, AgentInstallStatusReport>;
  settings: AgentInstallSettings;
}

/** POST /api/agent-install */
export interface AgentInstallStart {
  machineId: Id;
  kind: InstallableAgentKind;
}

export const AGENT_INSTALL_PATH = '/api/agent-install';
export const AGENT_INSTALL_SETTINGS_PATH = '/api/agent-install/settings';

/** Does the automatic-install setting cover a new machine with these labels? Which kinds, if so. */
export function autoInstallKindsFor(s: AgentInstallSettings, labels: readonly string[]): InstallableAgentKind[] {
  if (!s.autoInstall) return [];
  if (s.labels.length && !s.labels.some((l) => labels.includes(l))) return [];
  return INSTALLABLE_AGENT_KINDS.filter((k) => s.kinds.includes(k));
}

// ---------------------------------------------------------------------------
// Sanitisers: what a server keeps of what an agent sends
// ---------------------------------------------------------------------------

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const SHA256_RE = /^[a-f0-9]{64}$/;
const INTEGRITY_RE = /^sha(?:256|384|512)-[A-Za-z0-9+/]{20,100}={0,2}$/;
const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const CONTROL_RE = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/;

/** A version as a CLI reports it: plain characters, at most 60. */
export function cleanInstallVersion(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim().slice(0, 60).trim();
  return s && /^[0-9A-Za-z][0-9A-Za-z ._+()-]*$/.test(s) ? s : null;
}

function cleanCount(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.min(Math.floor(v), Number.MAX_SAFE_INTEGER) : undefined;
}

function cleanText(v: unknown, max: number): string {
  return typeof v === 'string' ? v.replace(new RegExp(CONTROL_RE.source, 'g'), ' ').trim().slice(0, max) : '';
}

export function isAgentInstallId(v: unknown): v is string {
  return typeof v === 'string' && ID_RE.test(v);
}

export function sanitizeInstallProgress(v: unknown): AgentInstallProgress | null {
  if (!isRecord(v) || !isAgentInstallId(v.installId) || !isInstallableAgentKind(v.kind)) return null;
  if (typeof v.phase !== 'string' || !(AGENT_INSTALL_PHASES as readonly string[]).includes(v.phase)) return null;
  const out: AgentInstallProgress = { installId: v.installId, kind: v.kind, phase: v.phase as AgentInstallPhase };
  const bytes = cleanCount(v.bytes);
  const total = cleanCount(v.total);
  if (bytes !== undefined) out.bytes = bytes;
  if (total !== undefined) out.total = total;
  return out;
}

/** A source is kept only when its URL is on the kind's pinned origins. */
export function sanitizeInstallSource(kind: InstallableAgentKind, v: unknown): AgentInstallSource | undefined {
  if (!isRecord(v) || typeof v.method !== 'string' || !(AGENT_INSTALL_METHODS as readonly string[]).includes(v.method)) return undefined;
  if (!isAllowedInstallUrl(kind, v.url)) return undefined;
  const out: AgentInstallSource = { method: v.method as AgentInstallMethod, url: v.url as string, sha256: typeof v.sha256 === 'string' && SHA256_RE.test(v.sha256) ? v.sha256 : null };
  if (typeof v.integrity === 'string' && INTEGRITY_RE.test(v.integrity)) out.integrity = v.integrity;
  const version = cleanInstallVersion(v.version);
  if (version) out.version = version;
  return out;
}

export function sanitizeInstallError(kind: InstallableAgentKind, v: unknown): AgentInstallError {
  const r = isRecord(v) ? v : {};
  const code = typeof r.code === 'string' && (AGENT_INSTALL_ERROR_CODES as readonly string[]).includes(r.code) ? (r.code as AgentInstallErrorCode) : 'install-failed';
  const base = agentInstallError(code, kind);
  return { what: cleanText(r.what, 200) || base.what, why: cleanText(r.why, 400) || base.why, next: cleanText(r.next, 300) || base.next, code };
}

export function sanitizeInstallResult(v: unknown): AgentInstallResult | null {
  if (!isRecord(v) || !isAgentInstallId(v.installId) || !isInstallableAgentKind(v.kind)) return null;
  const kind = v.kind;
  const ok = v.ok === true;
  const out: AgentInstallResult = { installId: v.installId, kind, ok, version: ok ? cleanInstallVersion(v.version) : null };
  const source = sanitizeInstallSource(kind, v.source);
  if (source) out.source = source;
  if (!ok) out.error = sanitizeInstallError(kind, v.error);
  return out;
}

export function sanitizeInstallStatus(v: unknown, now: Ts): AgentInstallStatusReport | null {
  if (!isRecord(v) || !Array.isArray(v.kinds)) return null;
  const kinds: AgentInstallKindStatus[] = [];
  for (const x of v.kinds.slice(0, 20)) {
    if (!isRecord(x) || !isInstallableAgentKind(x.kind) || kinds.some((k) => k.kind === x.kind)) continue;
    const installed = x.installed === true;
    const blocked = !installed && typeof x.blocked === 'string' && (AGENT_INSTALL_BLOCKS as readonly string[]).includes(x.blocked) ? (x.blocked as AgentInstallBlock) : undefined;
    const k: AgentInstallKindStatus = { kind: x.kind, installed, version: installed ? cleanInstallVersion(x.version) : null, installable: !installed && !blocked && x.installable === true };
    if (blocked) k.blocked = blocked;
    kinds.push(k);
  }
  const at = typeof v.checkedAt === 'number' && Number.isFinite(v.checkedAt) && v.checkedAt > 0 ? Math.min(v.checkedAt, now) : now;
  return { checkedAt: at, kinds };
}

/** PUT /api/agent-install/settings body: known fields only, labels as machine labels. */
export function cleanAgentInstallSettings(v: unknown, prev: AgentInstallSettings): Omit<AgentInstallSettings, 'updatedAt' | 'updatedBy'> | null {
  if (!isRecord(v)) return null;
  const autoInstall = typeof v.autoInstall === 'boolean' ? v.autoInstall : prev.autoInstall;
  const kinds = Array.isArray(v.kinds) ? INSTALLABLE_AGENT_KINDS.filter((k) => (v.kinds as unknown[]).includes(k)) : prev.kinds;
  let labels = prev.labels;
  if (Array.isArray(v.labels)) {
    labels = [];
    for (const l of v.labels.slice(0, 50)) {
      if (typeof l !== 'string') return null;
      const t = l.trim();
      if (!t || t.length > 60 || CONTROL_RE.test(t)) return null;
      if (!labels.includes(t)) labels.push(t);
    }
  }
  if (autoInstall && !kinds.length) return null;
  return { autoInstall, kinds, labels };
}

/**
 * Installs for work a person asked for (the assistant's reply on a machine without Claude Code,
 * ADR 0107 amendment of 24 Oct 2026). Both servers offer it to their other modules. The person's
 * role decides as for POST /api/agent-install, or the automatic-install setting when it names the
 * agent for that machine (requested by 'policy'); a running install of that agent is reused.
 */
export interface AgentInstallOnDemandApi {
  state(): AgentInstallHubState;
  installFor(machineId: Id, kind: InstallableAgentKind, person: { id: string; role: string; kind: string }): Promise<{ install: AgentInstallRecord } | { error: AgentInstallError }>;
}
